import { Routes } from '@angular/router';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { Route } from './core/enums/route.enum';
import { PAGE } from './core/constants/common.constant';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { partySearchContextChildGuard } from './core/guards/party-search-context.guard';
import { internalPartyContextGuard } from './core/guards/internal-party-context.guard';
import { UserRole } from './services/user/user.service';

/** Routes that load a customer's contracts — CNH/Admin need an active party search context. */
const PARTY_CUSTOMER_DATA = {
  allowedRoles: [UserRole.User, UserRole.CNHUser, UserRole.Admin],
  requiresPartySearch: true,
} as const;

export const routes: Routes = [
  {
    path: Route.LOGIN,
    title: PAGE.LOGIN,
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivateChild: [
      authGuard,
      roleGuard,
      internalPartyContextGuard,
      partySearchContextChildGuard,
    ],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./features/home-redirect/home-redirect.component').then(
            (m) => m.HomeRedirectComponent,
          ),
      },
      //////////////////////////// External User/Customer Routes  START ////////////////////////////

      {
        path: Route.DASHBOARD,
        title: PAGE.DASHBOARD,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: Route.PAYOUT,
        title: PAGE.PAYOUT,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/payout-request/payout-request.component').then(
            (m) => m.PayoutRequestComponent,
          ),
      },
      {
        path: Route.CHANGE_PAYMENT_METHOD,
        title: PAGE.CHANGE_PAYMENT_METHOD,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/change-payment-method/change-payment-method.component').then(
            (m) => m.ChangePaymentMethodComponent,
          ),
      },

      {
        path: Route.STATEMENT,
        title: PAGE.STATEMENT,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/repayment-and-statement/statement/statement.component').then(
            (m) => m.StatementComponent,
          ),
      },
      {
        path: Route.STATEMENT_DETAIL,
        title: PAGE.STATEMENT_DETAIL,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/repayment-and-statement/statement-detail/statement-detail.component').then(
            (m) => m.StatementDetailComponent,
          ),
      },

      {
        path: Route.FAQ,
        title: PAGE.FAQ,
        data: { allowedRoles: [UserRole.User, UserRole.CNHUser] },
        loadComponent: () => import('./features/faq/faq.component').then((m) => m.FaqComponent),
      },
      {
        path: Route.CUSTOMER_REQUEST_CHANGE,
        title: PAGE.CUSTOMER_REQUEST_CHANGE,
        data: { allowedRoles: [UserRole.User] },
        loadComponent: () =>
          import('./features/customer-request-change/customer-request-change.component').then(
            (m) => m.CustomerRequestChangeComponent,
          ),
      },
      {
        path: Route.CONTACT_US,
        title: PAGE.CONTACT_US,
        data: { allowedRoles: [UserRole.User] },
        loadComponent: () =>
          import('./features/contact-us/contact-us.component').then((m) => m.ContactUsComponent),
      },
      {
        path: Route.DISCOVER,
        title: PAGE.DISCOVER,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/discover/discover.component').then((m) => m.DiscoverComponent),
      },
      {
        path: Route.REPAYMENT_SCHEDULE,
        title: PAGE.REPAYMENT_SCHEDULE,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/repayment-and-statement/repayment/repayment.component').then(
            (m) => m.RepaymentComponent,
          ),
      },
      {
        path: Route.REPAYMENT_SCHEDULE_DETAIL,
        title: PAGE.REPAYMENT_SCHEDULE_DETAIL,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('@app/features/repayment-and-statement/repayment-schedule-detail/repayment-schedule-detail.component').then(
            (m) => m.RepaymentScheduleDetailComponent,
          ),
      },

      {
        path: Route.CONTRACT_DETAILS,
        title: PAGE.CONTRACT_DETAILS,
        data: { ...PARTY_CUSTOMER_DATA },
        loadComponent: () =>
          import('./features/contract-details/contract-details').then((m) => m.ContractDetails),
      },
      //////////////////////////// External User/Customer Routes  END ////////////////////////////
      {
        path: Route.SEARCH_USER,
        title: PAGE.SEARCH_USER,
        data: { allowedRoles: [UserRole.CNHUser, UserRole.Admin] },
        loadComponent: () =>
          import('./features/search/search.component').then((m) => m.SearchComponent),
      },

      {
        path: Route.USER_MANAGEMENT,
        title: PAGE.USER_MANAGEMENT,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHNewProfileUser] },
        loadComponent: () =>
          import('./features/user-management/user-management.component').then(
            (m) => m.UserManagementComponent,
          ),
      },
      {
        path: Route.DROPDOWN_CONFIGURATION,
        title: PAGE.DROPDOWN_CONFIGURATION,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHUser] },
        loadComponent: () =>
          import('@features/dropdown-configuration/dropdown-configuration.component').then(
            (m) => m.DropdownConfigurationComponent,
          ),
      },
      {
        path: Route.ANNOUNCEMENT,
        title: PAGE.ANNOUNCEMENT,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHUser, UserRole.CNHMarketingUser] },
        loadComponent: () =>
          import('@features/announcement/announcement.component').then(
            (m) => m.AnnouncementComponent,
          ),
      },
      {
        path: Route.UPLOAD_MARKETING_BANNER,
        title: PAGE.UPLOAD_MARKETING_BANNER,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHMarketingUser] },
        loadComponent: () =>
          import('./features/upload-marketing-banner/upload-marketing-banner.component').then(
            (m) => m.UploadMarketingBannerComponent,
          ),
      },
      {
        path: Route.CUSTOMER_FEEDBACK,
        title: PAGE.CUSTOMER_FEEDBACK,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHMarketingUser] },
        loadComponent: () =>
          import('./features/customer-feedback/admin-feedback/admin-feedback.component').then(
            (m) => m.CustomerFeedbackComponent,
          ),
      },
      {
        path: Route.CUSTOMER_FEEDBACK_USER,
        title: PAGE.CUSTOMER_FEEDBACK,
        data: { allowedRoles: [UserRole.User] },
        loadComponent: () =>
          import('./features/customer-feedback/customer-feedback-experience-form/customer-feedback-experience-form.component').then(
            (m) => m.CustomerFeedbackExperienceFormComponent,
          ),
      },
      {
        path: Route.ADMIN_TEMPLATES,
        title: PAGE.ADMIN_TEMPLATES,
        data: { allowedRoles: [UserRole.Admin] },
        loadComponent: () =>
          import('./features/admin-templates/admin-templates.component').then(
            (m) => m.AdminTemplatesComponent,
          ),
      },
      {
        path: Route.EDIT_TEMPLATE,
        title: PAGE.EDIT_TEMPLATE,
        data: { allowedRoles: [UserRole.Admin] },
        loadComponent: () =>
          import('./features/admin-templates/edit-template/edit-template.component').then(
            (m) => m.EditTemplateComponent,
          ),
      },
      {
        path: Route.NOTIFICATIONS,
        title: PAGE.NOTIFICATIONS,
        data: {
          allowedRoles: [
            UserRole.Admin,
            UserRole.CNHUser,
            UserRole.CNHMarketingUser,
            UserRole.User,
          ],
        },
        loadComponent: () =>
          import('./layouts/all-notifications/all-notifications.component').then(
            (m) => m.AllNotificationsComponent,
          ),
      },
      {
        path: Route.MY_PROFILE,
        title: PAGE.MY_PROFILE,
        loadComponent: () =>
          import('./features/my-profile/my-profile.component').then((m) => m.MyProfileComponent),
      },
      {
        path: Route.ADMIN_FAQ,
        title: PAGE.ADMIN_FAQ,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHMarketingUser] },
        loadComponent: () =>
          import('./features/faq/faq-admin/faq-admin.component').then((m) => m.FaqAdminComponent),
      },
      {
        path: `${Route.ADMIN_FAQ}/${Route.MANAGE_CATEGORY}`,
        title: PAGE.MANAGE_CATEGORY,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHMarketingUser] },
        loadComponent: () =>
          import('./features/faq/faq-admin/manage-category/manage-category.component').then(
            (m) => m.ManageCategoryComponent,
          ),
      },
      {
        path: Route.ADMIN_CONTACT_US,
        title: PAGE.CONTACT_US,
        data: { allowedRoles: [UserRole.Admin, UserRole.CNHMarketingUser] },
        loadComponent: () =>
          import('./features/contact-us/contact-us.component').then((m) => m.ContactUsComponent),
      },
      {
        path: Route.REQUEST_CHANGE,
        title: PAGE.REQUEST_CHANGE,
        data: { allowedRoles: [UserRole.Admin] },
        loadComponent: () =>
          import('./features/request-change/request-change.component').then(
            (m) => m.RequestChangeComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
